import React from 'react';
import { Button, Flex, TextField } from '@/components/ui';

import { IconSearch, IconX } from '@tabler/icons-react';
import { Table } from '@tanstack/react-table';

import { DataTableViewOptions } from './DataTableViewOptions';

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  globalFilter: string;
  onGlobalFilterChange: (value: string) => void;
  searchPlaceholder?: string;
  leftSlot?: React.ReactNode;
  centerSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  bottomSlot?: React.ReactNode;
  enableColumnVisibility?: boolean;
}

export function DataTableToolbar<TData>({
  table,
  globalFilter,
  onGlobalFilterChange,
  searchPlaceholder = 'Search...',
  leftSlot,
  centerSlot,
  rightSlot,
  bottomSlot,
  enableColumnVisibility = true,
}: DataTableToolbarProps<TData>) {
  const isFiltered =
    table.getState().columnFilters.length > 0 || !!globalFilter;

  return (
    <div className="data-table-toolbar">
      <div className="data-table-toolbar__main">
        <Flex className="data-table-toolbar__left" align="center" gap="3">
          {/* Search */}
          <TextField
            placeholder={searchPlaceholder}
            value={globalFilter ?? ''}
            onChange={e => onGlobalFilterChange(e.target.value)}
            startSlot={<IconSearch />}
            size="small"
            className="data-table-toolbar__search-input"
            // Reset filter button
            endSlot={
              isFiltered && (
                <Button
                  variant="soft"
                  size="xsmall"
                  isIconOnly
                  color="error"
                  onClick={() => {
                    table.resetColumnFilters();
                    onGlobalFilterChange('');
                  }}
                  className="data-table-toolbar__reset"
                  title={'Reset'}
                >
                  <IconX />
                </Button>
              )
            }
          />

          {/* Left slot (after search) */}
          {leftSlot}

          {/* Column Visibility */}
          {enableColumnVisibility && <DataTableViewOptions table={table} />}
        </Flex>

        {/* Center slot */}
        <div className="data-table-toolbar__center">{centerSlot}</div>

        {/* Right slot */}
        <div className="data-table-toolbar__right">{rightSlot}</div>
      </div>

      {/* Bottom slot */}
      {bottomSlot && (
        <div className="data-table-toolbar__bottom">{bottomSlot}</div>
      )}
    </div>
  );
}
